import { useCallback, useEffect, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { sessionsService } from "../services/sessionsService";
import type { SessionEntity } from "../types/sessions.types";

interface UseSessionsState {
  sessions: SessionEntity[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  revoke: (id: string) => Promise<void>;
  revokeOthers: () => Promise<number>;
}

export function useSessions(): UseSessionsState {
  const [sessions, setSessions] = useState<SessionEntity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const list = await sessionsService.list();
      setSessions(list);
    } catch (err) {
      setError(HttpApiService.getErrorMessage(err) || "Could not load sessions");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const revoke = useCallback(
    async (id: string) => {
      await sessionsService.revoke(id);
      await refresh();
    },
    [refresh],
  );

  const revokeOthers = useCallback(async () => {
    const result = await sessionsService.revokeOthers();
    await refresh();
    return result.revoked_count;
  }, [refresh]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { sessions, isLoading, error, refresh, revoke, revokeOthers };
}
